/**
 * Canonical PostHog event names fired from the web client.
 *
 * Human source of truth (all repos, including server-only events):
 *   docs/features/ANALYTICS_EVENTS.md
 *
 * `capture()` only accepts `EventName`, so an ad-hoc string literal or a typo
 * fails `tsc` rather than silently creating a junk event in PostHog.
 *
 * Server-fired events (registration_submitted, user_registered,
 * institution_connected, first_account_connected, …) are deliberately absent —
 * they must not be double-fired from the client.
 */
export const EVENTS = {
  // Registration
  REGISTRATION_PAGE_VIEWED: "registration_page_viewed",
  VERIFICATION_PENDING_VIEWED: "verification_pending_viewed",

  // Federated sign-in (Google / Apple). Client-side because the API only ever
  // hears about a run that produced a verified token: a dismissed popup or a
  // script that never loaded is invisible server-side. Success is deliberately
  // absent — user_registered / login_completed already cover it.
  //
  // Coverage is not symmetric between the two providers, and the gap is
  // Google's: their button is an iframe we cannot see into, so there is no
  // click and no dismissal to report. See the note in ANALYTICS_EVENTS.md.
  SSO_SIGN_IN_STARTED: "sso_sign_in_started",
  SSO_SIGN_IN_ABANDONED: "sso_sign_in_abandoned",
  SSO_SIGN_IN_FAILED: "sso_sign_in_failed",

  // Paywall: /subscribe on Stripe Checkout (PRD 87). Client-side only; the
  // api fires none of these. Names kept from the RevenueCat-era paywall.
  PAYWALL_VIEWED: "paywall_viewed",
  PLAN_SELECTED: "plan_selected",
  CHECKOUT_STARTED: "checkout_started",
  PURCHASE_COMPLETED: "purchase_completed",
  PURCHASE_CANCELLED: "purchase_cancelled",
  PURCHASE_FAILED: "purchase_failed",

  // Buying Greenback Business on /business/start (PRD 86 slice 5). Client-side
  // only, for now (Dave, 2026-10-09). PRD 86 §9's names
  // (business_signup_started, business_checkout_opened,
  // business_checkout_completed) are deliberately NOT used: they are kept for
  // the server, which will fire them from the checkout and the ingest, and a
  // web copy under the same name would double-count the day it does.
  // LoginForm on /register or /login with intent=business (the website's
  // Business CTAs), once per mount. The first step of the Business funnel.
  BUSINESS_INTENT_LANDED: "business_intent_landed",
  BUSINESS_START_VIEWED: "business_start_viewed",
  BUSINESS_CHECKOUT_STARTED: "business_checkout_started",
  BUSINESS_CHECKOUT_REFUSED: "business_checkout_refused",
  BUSINESS_PURCHASE_CONFIRMED: "business_purchase_confirmed",
  BUSINESS_NAMED: "business_named",

  // First session
  APP_AUTHENTICATED: "app_authenticated",
  ACCOUNTS_EMPTY_STATE_VIEWED: "accounts_empty_state_viewed",
  CONNECT_ACCOUNT_CTA_CLICKED: "connect_account_cta_clicked",

  // Activation
  INSTITUTION_SEARCH_PERFORMED: "institution_search_performed",
  INSTITUTION_SELECTED: "institution_selected",
  ACCOUNT_LINK_STARTED: "account_link_started",
  ACCOUNT_LINK_ABANDONED: "account_link_abandoned",
  ACCOUNT_LINK_FAILED: "account_link_failed",
  CONNECTION_SUCCESS_SHOWN: "connection_success_shown",

  // The Live sample (PRD 117, D10). The ONLY events a demo session sends:
  // capture() drops everything else while isDemo(), and nobody is identified,
  // because every visitor of a persona shares one user. Tagged with the
  // `demo_persona` super property.
  DEMO_STARTED: "demo_started",
  DEMO_READ_ONLY_HIT: "demo_read_only_hit",
  DEMO_CTA_CLICKED: "demo_cta_clicked",
} as const;

/** What a demo session may send. Everything else is dropped in demo mode. */
export const DEMO_EVENTS: readonly EventName[] = [
  EVENTS.DEMO_STARTED,
  EVENTS.DEMO_READ_ONLY_HIT,
  EVENTS.DEMO_CTA_CLICKED,
];

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];
